/**
 * Venda na Rota — etapa 5, Resultado: o que aconteceu com a venda e com o documento escolhido,
 * e as ações seguintes (imprimir o Pedido de Venda, acompanhar a NF-e, emitir a NF-e depois,
 * nova venda). A impressão é separada da venda: se a impressora falhar, nada muda na venda.
 */
import { SALES_STATUS_LABEL, formatMoney, type Page, type SalesOrder } from "@cafegestor/shared";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Alert, Button, Icon, StatusBadge } from "@cafegestor/ui";
import { apiFetch, errorMessage } from "@cafegestor/client";
import type { RouteIntent } from "@cafegestor/client/offline";
import { OrderDocuments } from "./OrderDocuments";
import type { DocumentChoice } from "./rota-lib";

export function RotaResult({
  intent,
  document,
  onNewSale,
  onShowSales,
}: {
  intent: RouteIntent;
  document: DocumentChoice;
  onNewSale: () => void;
  onShowSales: () => void;
}) {
  const r = intent.result;
  const footer = (
    <div className="grid grid-cols-1 gap-2 min-[420px]:grid-cols-2">
      <Button size="lg" onClick={onNewSale}>
        <Icon name="plus" size={20} strokeWidth={2.4} />
        Nova venda
      </Button>
      <Button size="lg" variant="secondary" onClick={onShowSales}>
        Vendas desta carga
      </Button>
    </div>
  );

  if (intent.state !== "concluida" || !r) {
    return (
      <div className="space-y-4" aria-label="Resultado da venda">
        <Hero
          tone="warn"
          title="Venda guardada neste aparelho"
          text="Sem conexão (ou a conexão caiu no envio). A venda será enviada sozinha quando a conexão voltar, com a mesma chave — sem duplicar venda, estoque, financeiro ou nota."
        />
        <Alert tone="info">
          {document === "nfe"
            ? "Nenhuma nota foi emitida ainda: a NF-e é pedida quando a venda chegar ao servidor."
            : "O Pedido de Venda pode ser impresso assim que a venda for registrada (ele leva o número do pedido)."}
        </Alert>
        {footer}
      </div>
    );
  }

  const f = r.fiscal;
  return (
    <div className="space-y-4" aria-label="Resultado da venda">
      <Hero
        tone="ok"
        title={`Venda ${r.orderNumber} registrada`}
        text={`Total ${formatMoney(r.totalAmount)}. Estoque da carga e financeiro já atualizados.`}
      />
      {f.state === "emitido" && (
        <Alert tone="info">
          Nota nº {f.number} enviada para autorização. Acompanhe abaixo — não é preciso esperar
          nesta tela: a emissão continua no servidor.
        </Alert>
      )}
      {f.state === "bloqueado" && (
        <Alert tone="warn">
          <strong>A venda foi registrada, mas a nota não pôde ser pedida:</strong>
          <ul className="mt-1 list-disc pl-5">
            {f.issues.map((i, n) => (
              <li key={`${i.code}-${n}`}>{i.message}</li>
            ))}
          </ul>
          <span className="mt-1 block">
            Corrija o cadastro e emita a NF-e pelo mesmo pedido (abaixo), sem repetir a venda.
          </span>
        </Alert>
      )}
      {f.state === "desligado" && (
        <Alert tone="warn">
          A venda foi registrada, mas a emissão de notas está desligada: {f.message} O pedido fica
          aguardando faturamento.
        </Alert>
      )}
      <OrderDocuments orderId={r.orderId} />
      {footer}
    </div>
  );
}

function Hero({ tone, title, text }: { tone: "ok" | "warn"; title: string; text: string }) {
  return (
    <div
      role="status"
      className={`flex items-start gap-3 rounded-2xl border p-4 ${
        tone === "ok"
          ? "border-success/30 bg-success-soft text-success-strong"
          : "border-warning/30 bg-warning-soft text-warning-strong"
      }`}
    >
      <span
        aria-hidden
        className={`flex size-11 shrink-0 items-center justify-center rounded-full ${
          tone === "ok" ? "bg-success text-white" : "bg-warning text-white"
        }`}
      >
        <Icon name={tone === "ok" ? "check" : "wifiOff"} size={22} strokeWidth={2.4} />
      </span>
      <div className="min-w-0">
        <h2 className="text-lg font-bold leading-snug">{title}</h2>
        <p className="text-sm">{text}</p>
      </div>
    </div>
  );
}

/** Vendas já registradas na carga: reimprimir o pedido e emitir a NF-e depois. */
export function LoadSales({ loadId, online }: { loadId: string; online: boolean }) {
  const [open, setOpen] = useState<string | null>(null);
  const list = useQuery({
    queryKey: ["vendas", "pedidos", "carga", loadId],
    queryFn: () =>
      apiFetch<Page<SalesOrder>>(
        `/vendas/pedidos?loadId=${encodeURIComponent(loadId)}&status=todos&limit=50`,
      ),
    enabled: online && loadId !== "",
  });
  if (!online)
    return (
      <p className="rounded-xl border border-dashed border-input px-4 py-5 text-center text-sm text-muted-foreground">
        A lista de vendas da carga precisa de conexão.
      </p>
    );
  if (list.error) return <Alert tone="error">{errorMessage(list.error)}</Alert>;
  if (!list.data)
    return (
      <p role="status" className="text-sm text-muted-foreground">
        Carregando as vendas da carga…
      </p>
    );
  if (list.data.items.length === 0)
    return (
      <p className="rounded-xl border border-dashed border-input px-4 py-5 text-center text-sm text-muted-foreground">
        Nenhuma venda registrada nesta carga ainda.
      </p>
    );
  return (
    <ul aria-label="Vendas desta carga" className="space-y-2">
      {list.data.items.map((o) => {
        const on = open === o.id;
        return (
          <li key={o.id} className="rounded-2xl border border-border bg-card">
            <button
              type="button"
              aria-expanded={on}
              onClick={() => setOpen(on ? null : o.id)}
              className="flex min-h-14 w-full items-center gap-3 px-4 py-3 text-left"
            >
              <span className="min-w-0 flex-1">
                <span className="block font-semibold break-words">
                  {o.number} · {o.customerName}
                </span>
                <span className="block text-[13px] text-muted-foreground">
                  {new Date(o.createdAt).toLocaleString("pt-BR", {
                    dateStyle: "short",
                    timeStyle: "short",
                  })}{" "}
                  · {o.paymentMethodName ?? "—"}
                </span>
              </span>
              <span className="shrink-0 text-right">
                <span className="block font-bold tabular-nums">{formatMoney(o.totalAmount)}</span>
                {o.status !== "confirmado" && (
                  <StatusBadge tone="neutral">{SALES_STATUS_LABEL[o.status]}</StatusBadge>
                )}
              </span>
              <Icon
                name="chevronRight"
                size={18}
                className={`shrink-0 text-muted-foreground transition-transform ${on ? "rotate-90" : ""}`}
              />
            </button>
            {on && (
              <div className="border-t border-border/70 p-3">
                <OrderDocuments orderId={o.id} />
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );
}
